import { useCallback } from 'react';
import { useSiteConfigDispatch, useSiteConfigHistory } from '../context/SiteConfigContext';
import { useBuilder } from '../context/BuilderContext';
import { useToast } from '../components/Toast/ToastContext';
import { useT } from './useT';

/**
 * The one place that deletes a block. Every delete affordance (the Delete
 * key in App.js, BlockControls' trash button, PropertyPanel's Remove button,
 * and any future one — Task 14's block outline included) calls this instead
 * of re-running the dispatch/deselect/toast sequence itself, so the two
 * contracts (confirm vs. no confirm) that used to exist here can't drift
 * apart again.
 *
 * Deletes immediately — no confirmation — and raises an Undo toast, since
 * undo/redo makes a confirm dialog redundant on a destructive-but-reversible
 * action.
 */
export function useDeleteBlock() {
  const dispatch = useSiteConfigDispatch();
  const { selectedBlockId, deselectBlock } = useBuilder();
  const { showToast } = useToast();
  const { undo } = useSiteConfigHistory();
  const t = useT();

  return useCallback((pageKey, blockId) => {
    dispatch({ type: 'REMOVE_BLOCK', pageKey, blockId });
    // Only collapse the selection (and the sheet along with it, via
    // deselectBlock's sheetSnap: 'peek') when the block that was deleted is
    // the one that was selected. Deleting a different block from the
    // Outline — the selected block is left untouched — must not throw the
    // sheet shut on the list the user is still working in.
    if (blockId === selectedBlockId) {
      deselectBlock();
    }
    showToast({
      message: t('editor.blockDeleted', 'Block deleted'),
      actionLabel: t('editor.undo', 'Undo'),
      onAction: undo,
    });
  }, [dispatch, selectedBlockId, deselectBlock, showToast, undo, t]);
}
